import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { HeroLink } from './hero.types';

/** Internal routes use the router; anything else is a plain anchor. */
function isInternal(href: string): boolean {
  return href.startsWith('/') && !href.startsWith('//');
}

function ActionLink({
  link,
  children,
  className,
  style,
}: {
  link: HeroLink;
  children: React.ReactNode;
  className: string;
  style?: React.CSSProperties;
}): JSX.Element {
  if (isInternal(link.href)) {
    return (
      <Link to={link.href} className={className} style={style}>
        {children}
      </Link>
    );
  }
  return (
    <a href={link.href} className={className} style={style} rel="noreferrer">
      {children}
    </a>
  );
}

const FOCUS =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--hero-glow-secondary)]';

export function HeroActions({
  primary,
  secondary,
}: {
  primary: HeroLink;
  secondary: HeroLink;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
      <ActionLink
        link={primary}
        className={`hero-cta-sheen group inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-semibold text-white shadow-[0_20px_55px_-16px_var(--hero-glow-primary)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_26px_70px_-14px_var(--hero-glow-primary)] ${FOCUS}`}
        style={{
          backgroundImage: 'linear-gradient(135deg, #8b57ff 0%, #6d3bff 55%, #4f22cc 100%)',
        }}
      >
        <span className="relative z-10 inline-flex items-center gap-2">
          {primary.label}
          <ArrowRight
            className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
            aria-hidden
          />
        </span>
      </ActionLink>

      <ActionLink
        link={secondary}
        className={`group inline-flex items-center justify-center rounded-full border border-[var(--hero-border-strong)] bg-[var(--hero-glass-2)] px-8 py-4 text-sm font-semibold text-[var(--hero-text)] backdrop-blur-md transition-all duration-200 hover:border-[var(--hero-glow-secondary)] hover:bg-[color-mix(in_srgb,var(--hero-glow-primary)_12%,transparent)] ${FOCUS}`}
      >
        {secondary.label}
      </ActionLink>
    </div>
  );
}
